/**
 * The case for on-device inference.
 *
 * The honest half of this page is the point of it: hosted models are still
 * ahead, and saying so is what makes the rest believable. A page that only
 * argued for local would read as marketing about a feature the visitor can
 * check in five minutes.
 */

import { useI18n } from '@/lib/i18n';
import { PageHead, Section } from '@/components/site/Page';
import '@/components/site/Page.scss';
import './LocalAI.scss';

const WHAT = ['chain', 'engine', 'nokey'] as const;

/**
 * What each route means for your data, as four questions answered three ways.
 * Values are locale keys; the shape is here so the table cannot say something
 * different from the sentence above it.
 */
const MATRIX: readonly [string, string, string, string][] = [
  ['localai.row.leaves', 'localai.val.toVendor', 'localai.val.toVendor', 'localai.val.never'],
  ['localai.row.hear', 'localai.val.vendorCan', 'localai.val.vendorCan', 'localai.val.nobody'],
  ['localai.row.offline', 'localai.val.no', 'localai.val.no', 'localai.val.yes'],
  ['localai.row.costs', 'localai.val.vendorBills', 'localai.val.meteredByUs', 'localai.val.free'],
];

export function LocalAI() {
  const { t } = useI18n();

  return (
    <div className="site-page localai">
      <PageHead
        kicker={t('nav.localAi')}
        title={t('localai.page.title')}
        lead={t('localai.page.lead')}
      />

      <Section title={t('localai.what.title')}>
        <div className="localai__cards">
          {WHAT.map((k) => (
            <article className="localai__card" key={k}>
              <h3>{t(`localai.${k}.title`)}</h3>
              <p>{t(`localai.${k}.body`)}</p>
            </article>
          ))}
        </div>
      </Section>

      {/* Two cards rather than a paragraph, because they are a pair: what is
          true now set against what we expect. Run together as prose the second
          reads as an excuse for the first. */}
      <Section title={t('localai.honest.title')} lead={t('localai.honest.lead')}>
        <div className="localai__pair">
          <article className="localai__card localai__card--roomy">
            <div className="site-label localai__label">{t('localai.today.title')}</div>
            <p>{t('localai.today.body')}</p>
          </article>
          <article className="localai__card localai__card--roomy localai__card--dark">
            <div className="site-label localai__label localai__label--alt">
              {t('localai.tomorrow.title')}
            </div>
            <p>
              {t('localai.tomorrow.body')}{' '}
              <strong>{t('localai.tomorrow.strong')}</strong>
            </p>
          </article>
        </div>
      </Section>

      <Section title={t('localai.data.title')} lead={t('localai.data.lead')}>
        <div className="localai__table" role="table">
          <div className="localai__row localai__row--head" role="row">
            <span />
            <span className="site-label" role="columnheader">{t('localai.col.ownKey')}</span>
            <span className="site-label" role="columnheader">{t('localai.col.account')}</span>
            <span className="site-label" role="columnheader">{t('localai.col.onDevice')}</span>
          </div>
          {MATRIX.map(([row, ...cells]) => (
            <div className="localai__row" role="row" key={row}>
              <span className="localai__row-name" role="rowheader">{t(row)}</span>
              {cells.map((c, i) => (
                <span key={i} role="cell">{t(c)}</span>
              ))}
            </div>
          ))}
        </div>
      </Section>
    </div>
  );
}
